import { Box, Step, StepButton, StepLabel, Stepper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { StepIconProps } from '@mui/material/StepIcon';

import { stepperComponentStyles } from './StepperComponent.styles';

export interface StepperItem<TId extends string | number = string | number> {
  id: TId;
  label: string;
  completed?: boolean;
  disabled?: boolean;
}

interface StepperComponentProps<TId extends string | number> {
  steps: readonly StepperItem<TId>[];
  activeStep: TId;
  ariaLabel: string;
  onStepChange: (stepId: TId) => void;
}

function StepNumber({active = false, completed = false, icon}: StepIconProps) {
  const theme = useTheme();

  return (
    <Box component="span" aria-hidden="true" sx={stepperComponentStyles.stepNumber(theme, active, completed)}>
      {completed ? '\u2713' : icon}
    </Box>
  );
}

export function StepperComponent<TId extends string | number>({steps, activeStep, ariaLabel, onStepChange}: StepperComponentProps<TId>) {
  const theme = useTheme();
  const activeStepIndex = steps.findIndex((step) => step.id === activeStep);
  const activeStepItem = steps[activeStepIndex];

  return (
    <Box sx={stepperComponentStyles.container()}>
      {activeStepItem && (
        <Box component="p" aria-live="polite" aria-atomic="true" aria-current="step" sx={stepperComponentStyles.mobileProgress(theme)}>
          Step {activeStepIndex + 1} of {steps.length} — {activeStepItem.label}
        </Box>
      )}
      <Stepper nonLinear alternativeLabel activeStep={activeStepIndex} aria-label={ariaLabel} sx={stepperComponentStyles.stepper(theme)}>
        {steps.map((step) => (
          <Step key={step.id} completed={step.completed}>
            <StepButton type="button" disableRipple disabled={step.disabled} onClick={() => onStepChange(step.id)}>
              <StepLabel slots={{stepIcon: StepNumber}}>{step.label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
